import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Gift, LogOut, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { useSubscription, PLAN_DETAILS } from '@/contexts/SubscriptionContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { PromoCodeRedeemer } from '@/components/subscription/PromoCodeRedeemer';
import { pickByLanguage } from '@/lib/language';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLongPressTooltip } from '@/hooks/useLongPressTooltip';

export function UserAccountMenu() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const {
    openCustomerPortal,
    hasStripeCustomer,
    planId,
  } = useSubscription();

  const planBadgeColor: Record<string, string> = {
    business: 'bg-gradient-to-br from-purple-500 to-fuchsia-500',
    pro: 'bg-gradient-to-br from-amber-400 to-orange-500',
    starter: 'bg-primary',
  };

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);
  const avatarTooltip = useLongPressTooltip();

  // For email-login users, user_metadata.avatar_url is empty — fall back to the
  // Qlivar profile avatar they set inside the app.
  useEffect(() => {
    if (!user) return;
    // Social logins already have avatar_url in user_metadata; skip the DB query.
    if (user.user_metadata?.avatar_url) return;
    supabase
      .from('profiles')
      .select('avatar_url')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.avatar_url) setProfileAvatarUrl(data.avatar_url);
      });
  }, [user?.id, user?.user_metadata?.avatar_url]);

  // Priority: social avatar → Qlivar profile avatar → initials fallback
  const resolvedAvatarUrl = user?.user_metadata?.avatar_url || profileAvatarUrl;

  if (!user) return null;

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error(t('Falha ao sair', 'Failed to sign out'));
      return;
    }

    toast.success(t('Sessão encerrada com sucesso', 'Signed out successfully'));
    setLogoutConfirmOpen(false);
    navigate('/auth');
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip open={avatarTooltip.open} onOpenChange={avatarTooltip.setOpen}>
          <DropdownMenu modal={false} onOpenChange={() => avatarTooltip.setOpen(false)}>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  {...avatarTooltip.triggerProps}
                  className="relative h-9 w-9 rounded-full border border-[#eadbcc] bg-[#fff8f0]/80 p-0 backdrop-blur-sm transition-colors duration-150 hover:border-[#dcc5b0] hover:bg-[#fff3e9] focus-visible:ring-2 focus-visible:ring-[#f07f45]/30 focus-visible:outline-none"
                >
                  <Avatar className="h-7 w-7 rounded-full">
                    <AvatarImage src={resolvedAvatarUrl ?? undefined} alt="Avatar" />
                    <AvatarFallback className="rounded-full bg-[#fde9d6] text-[#f07f45] text-xs font-semibold">
                      {(user.email || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {planId !== 'free' && (
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background ${planBadgeColor[planId] ?? ''}`}
                      title={PLAN_DETAILS[planId]?.name}
                    />
                  )}
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {user.user_metadata?.full_name || user.email}
            </TooltipContent>
            <DropdownMenuContent align="end" collisionPadding={8} className="w-[calc(100vw-16px)] max-w-[240px] rounded-2xl border-[#eadbcc] bg-[#fffaf5] p-1">
          <div className="flex items-center gap-2 rounded-xl bg-[#f6ebdf] p-2.5">
            <Avatar className="h-8 w-8">
              <AvatarImage src={resolvedAvatarUrl ?? undefined} alt="Avatar" />
              <AvatarFallback className="bg-[#fde9d6] text-[#f07f45] text-sm font-semibold">
                {(user.email || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-[#2f241e] truncate max-w-[160px]">
                {user.user_metadata?.full_name || user.email}
              </span>
              <span className="text-xs text-[#9a8a7d] truncate max-w-[160px]">
                {user.email}
              </span>
            </div>
          </div>
          <DropdownMenuSeparator />
          {!adminLoading && isAdmin && (
            <DropdownMenuItem onClick={() => navigate('/admin')} className="rounded-lg text-[#2f241e] hover:bg-[#f6ebdf] focus:bg-[#f6ebdf] cursor-pointer">
              <Shield className="mr-2 h-4 w-4 text-[#9a8a7d]" />
              Admin
            </DropdownMenuItem>
          )}
          {hasStripeCustomer && (
            <DropdownMenuItem onClick={openCustomerPortal} className="rounded-lg text-[#2f241e] hover:bg-[#f6ebdf] focus:bg-[#f6ebdf] cursor-pointer">
              <CreditCard className="mr-2 h-4 w-4 text-[#9a8a7d]" />
              {t('Faturamento', 'Billing')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setPromoOpen(true)} className="rounded-lg text-[#2f241e] hover:bg-[#f6ebdf] focus:bg-[#f6ebdf] cursor-pointer">
            <Gift className="mr-2 h-4 w-4 text-[#9a8a7d]" />
            {t('Código promocional', 'Promo Code')}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-[#eadbcc]" />
          <DropdownMenuItem
            onClick={() => setLogoutConfirmOpen(true)}
            className="rounded-lg text-[#c0392b] hover:bg-[#fdf0ee] focus:bg-[#fdf0ee] focus:text-[#c0392b] cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4 opacity-70" />
            {t('Sair', 'Sign out')}
          </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>

      <AlertDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Sair da conta?', 'Sign out?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('Você precisará fazer login novamente para acessar seu dashboard.', 'You will need to sign in again to access your dashboard.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancelar', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSignOut}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/40"
            >
              {t('Sair', 'Sign out')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PromoCodeRedeemer controlled open={promoOpen} onOpenChange={setPromoOpen} />
    </>
  );
}

interface UserAccountMenuMobileProps {
  onNavigate?: () => void;
}

export function UserAccountMenuMobile({ onNavigate }: UserAccountMenuMobileProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { language } = useLanguage();
  const t = (pt: string, en: string) => pickByLanguage(language, { pt, en });
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  const {
    openCustomerPortal,
    hasStripeCustomer,
  } = useSubscription();

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);

  if (!user) return null;

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error(t('Falha ao sair', 'Failed to sign out'));
      return;
    }

    toast.success(t('Sessão encerrada com sucesso', 'Signed out successfully'));
    setLogoutConfirmOpen(false);
    onNavigate?.();
    navigate('/auth');
  };

  const handleNavigate = (path: string) => {
    onNavigate?.();
    navigate(path);
  };

  const handleOpenPromo = () => {
    onNavigate?.();
    setPromoOpen(true);
  };

  const handleOpenBilling = () => {
    onNavigate?.();
    openCustomerPortal();
  };

  return (
    <>
      <div className="space-y-2">
        {!adminLoading && isAdmin && (
          <Button
            variant="outline"
            className="w-full h-11 justify-start transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:ring-primary/40"
            onClick={() => handleNavigate('/admin')}
          >
            <Shield className="mr-2 h-4 w-4" />
            Admin
          </Button>
        )}

        {hasStripeCustomer && (
          <Button
            variant="outline"
            className="w-full h-11 justify-start transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:ring-primary/40"
            onClick={handleOpenBilling}
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {t('Faturamento', 'Billing')}
          </Button>
        )}

        <Button
          variant="outline"
          className="w-full h-11 justify-start transition-colors hover:border-primary/40 hover:bg-primary/5 focus-visible:ring-primary/40"
          onClick={handleOpenPromo}
        >
          <Gift className="mr-2 h-4 w-4" />
          {t('Código promocional', 'Promo Code')}
        </Button>

        <Button
          variant="ghost"
          className="w-full h-11 justify-start text-destructive hover:text-destructive transition-colors hover:bg-destructive/10 focus-visible:ring-destructive/40"
          onClick={() => setLogoutConfirmOpen(true)}
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('Sair', 'Sign out')}
        </Button>
      </div>

      <AlertDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Sair da conta?', 'Sign out?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('Você precisará fazer login novamente para acessar seu dashboard.', 'You will need to sign in again to access your dashboard.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Cancelar', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSignOut}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/40"
            >
              {t('Sair', 'Sign out')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PromoCodeRedeemer controlled open={promoOpen} onOpenChange={setPromoOpen} />
    </>
  );
}
